import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  VaRadio,
  VaRadioOption,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import pageNames from './pageNames';
import SubTaskContainerHeader from '../SubTaskContainerHeader';
import {
  trackSubtaskStepView,
  trackActiveDutySelection,
  trackSubtaskValidationError,
} from '../../utils/tracking/features/subtaskTracking';

const dataKey = 'view:isActiveDuty';

const content = {
  label: 'Are you on active duty right now?',
  errorMessage: 'Please choose a selection',
};

// values should be strings
const options = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const optionValues = options.map(option => option.value);

const getNextPage = data =>
  data[dataKey] === optionValues[0] ? pageNames.rad : pageNames.appeals;

const validate = data => optionValues.includes(data[dataKey]);

/**
 * Active duty page
 * @param {Object} data - subtask data
 * @param {Boolean} error - page submitted & error state
 * @param {Function} setPageData - updates subtask data
 * @returns {JSX}
 */
const ActiveDuty = ({ data = {}, error, setPageData }) => {
  const { addStep, getHistory } = usePathHistory();

  // ──────────────────────────────────────────────
  // STEP VIEW TRACKING
  // ──────────────────────────────────────────────
  useEffect(() => {
    trackSubtaskStepView({
      stepName: pageNames.start,
      pathHistoryContext: { addStep, getHistory },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ──────────────────────────────────────────────
  // ERROR TRACKING
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (error) {
      trackSubtaskValidationError(
        pageNames.start,
        'active-duty',
        content.errorMessage,
      );
    }
  }, [error]);

  const handlers = {
    setActiveDutyChoice: ({ target }) => {
      const { value } = target;
      setPageData({ [dataKey]: value || null });
    },
  };

  return (
    <>
      <SubTaskContainerHeader />
      <VaRadio
        label={content.label}
        labelHeaderLevel="2"
        error={error ? content.errorMessage : null}
        onRadioOptionSelected={handlers.setActiveDutyChoice}
        className="vads-u-margin-top--0"
        required
      >
        {options.map(({ value, label }) => (
          <VaRadioOption
            key={value}
            className="vads-u-margin-y--2"
            name="activeDuty"
            id={value}
            value={value}
            label={label}
            checked={value === data[dataKey]}
          />
        ))}
      </VaRadio>
    </>
  );
};

ActiveDuty.propTypes = {
  data: PropTypes.shape({}),
  error: PropTypes.bool,
  setPageData: PropTypes.func,
};

export default {
  name: pageNames.start,
  component: ActiveDuty,
  validate,
  back: pageNames.landing,
  next: getNextPage,
  // ──────────────────────────────────────────────
  // USER INPUT TRACKING
  // ──────────────────────────────────────────────
  onContinue: data => {
    const isActiveDuty = data[dataKey] === 'yes';
    trackActiveDutySelection(isActiveDuty);
  },
};
